import { word } from "@modules/core/utils/i18n"
import { MemberDetailedEntity } from "@modules/members/entities/memberEntity"
import { PublicProfileDetails } from "@modules/profile/publicProfile/components/publicProfileDetails"
import { HUText } from "@modules/ui/components/huText"
import { Ambulance, Dumbbell, House, Mail, Phone } from "lucide-react"
import { Skeleton } from "primereact/skeleton"
import { useTheme } from "styled-components"

type Props = {
  memberDetailed?: MemberDetailedEntity | null
} & React.HTMLAttributes<HTMLDivElement>
export const MemberTabProfile: React.FC<Props> = ({ memberDetailed, ...props }) => {
  const theme = useTheme()

  // SKELETON
  if (!memberDetailed) {
    return (
      <div className="flex flex-column w-full gap-6" {...props}>
        {/* CONTACT */}
        <div className="flex flex-column mt-2 gap-3">
          <HUText fontStyle="TM" color={theme.mainWhite}>
            {word("box.member.table.profile.contact")}
          </HUText>
          <div className="flex">
            <div className="flex flex-1 gap-2 align-items-center">
              <Mail strokeWidth="1" />
              <Skeleton width="12rem" borderRadius="4px" />
            </div>
          </div>
          {/* ADDRESS */}
          <div className="flex flex-row gap-2">
            <House strokeWidth="1" />

            <div className="flex flex-1 flex-column gap-2">
              <HUText className="uppercase" fontStyle="BS" color={theme.neutralColor600}>
                {word("box.member.table.profile.address.label")}
              </HUText>
              <Skeleton width="7rem" borderRadius="4px" />

              <div className="flex justify-content-between">
                <div className="flex flex-column gap-1">
                  <HUText className="uppercase mt-2" fontStyle="BS" color={theme.neutralColor600}>
                    {word("box.member.table.profile.address.zipCode")}
                  </HUText>
                  <Skeleton width="7rem" borderRadius="4px" />
                </div>

                <div className="flex flex-column gap-1">
                  <HUText className="uppercase mt-2" fontStyle="BS" color={theme.neutralColor600}>
                    {word("box.member.table.profile.address.city")}
                  </HUText>
                  <Skeleton width="7rem" borderRadius="4px" />
                </div>

                <div className="flex flex-column gap-1">
                  <HUText className="uppercase mt-2" fontStyle="BS" color={theme.neutralColor600}>
                    {word("box.member.table.profile.address.country")}
                  </HUText>
                  <Skeleton width="7rem" borderRadius="4px" />
                </div>
              </div>
            </div>
          </div>

          {/* EMERGENCY CONTACT */}
          <div className="flex flex-row gap-2">
            <Ambulance strokeWidth="1" />
            <div className="flex flex-column gap-1">
              <HUText className="uppercase" fontStyle="BS" color={theme.neutralColor600}>
                {word("box.member.table.profile.emergencyContact")}
              </HUText>
              <Skeleton width="14.5rem" borderRadius="4px" />
            </div>
          </div>
          <div className="flex flex-row gap-2">
            <Dumbbell strokeWidth="1" />
            <div className="flex flex-column gap-1">
              <HUText className="uppercase" fontStyle="BS" color={theme.neutralColor600}>
                {word("box.member.table.profile.startDate")}
              </HUText>
              <Skeleton width="5.5rem" borderRadius="4px" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-column w-full gap-6" {...props}>
      {/* CONTACT */}
      <div className="flex flex-column mt-2 gap-3">
        <HUText fontStyle="TM" color={theme.mainWhite}>
          {word("box.member.table.profile.contact")}
        </HUText>
        <div className="flex">
          <div className="flex flex-1 gap-2">
            <Mail strokeWidth="1" />
            <HUText fontStyle="TS" color={theme.mainWhite}>
              {memberDetailed.email}
            </HUText>
          </div>
          {memberDetailed.phoneNumber && (
            <div className="flex gap-2">
              <Phone strokeWidth="1" />
              <HUText fontStyle="TS" color={theme.mainWhite}>
                {memberDetailed.phoneNumber}
              </HUText>
            </div>
          )}
        </div>
      </div>
      {/* ADDRESS */}
      <div className="flex flex-row gap-2">
        <House strokeWidth="1" />

        <div className="flex flex-1 flex-column gap-2 mt-1">
          <HUText className="uppercase" fontStyle="BS" color={theme.neutralColor600}>
            {word("box.member.table.profile.address.label")}
          </HUText>
          <HUText fontStyle="TS" color={theme.mainWhite}>
            {memberDetailed.address?.address ? memberDetailed.address.address : word("global.notProvided")}
          </HUText>

          {memberDetailed.address?.additionalInformation && (
            <>
              <HUText className="uppercase mt-2" fontStyle="BS" color={theme.neutralColor600}>
                {word("box.member.table.profile.address.supplement")}
              </HUText>
              <HUText fontStyle="TS" color={theme.mainWhite}>
                {memberDetailed.address.additionalInformation}
              </HUText>
            </>
          )}

          <div className="flex justify-content-between">
            <div className="flex flex-column gap-1">
              <HUText className="uppercase mt-2" fontStyle="BS" color={theme.neutralColor600}>
                {word("box.member.table.profile.address.zipCode")}
              </HUText>
              <HUText fontStyle="TS" color={theme.mainWhite}>
                {memberDetailed.address?.postalCode ? memberDetailed.address.postalCode : word("global.notProvided")}
              </HUText>
            </div>

            <div className="flex flex-column gap-1">
              <HUText className="uppercase mt-2" fontStyle="BS" color={theme.neutralColor600}>
                {word("box.member.table.profile.address.city")}
              </HUText>
              <HUText fontStyle="TS" color={theme.mainWhite}>
                {memberDetailed.address?.city ? memberDetailed.address.city : word("global.notProvided")}
              </HUText>
            </div>

            <div className="flex flex-column gap-1">
              <HUText className="uppercase mt-2" fontStyle="BS" color={theme.neutralColor600}>
                {word("box.member.table.profile.address.country")}
              </HUText>
              <HUText fontStyle="TS" color={theme.mainWhite}>
                {memberDetailed.address?.country ? memberDetailed.address.country : word("global.notProvided")}
              </HUText>
            </div>
          </div>
        </div>
      </div>

      {/* EMERGENCY CONTACT */}
      <div className="flex flex-row gap-2">
        <Ambulance strokeWidth="1" />
        <div className="flex flex-column gap-1 mt-1">
          <HUText className="uppercase" fontStyle="BS" color={theme.neutralColor600}>
            {word("box.member.table.profile.emergencyContact")}
          </HUText>
          {memberDetailed.emergencyInformation ? (
            <HUText fontStyle="TS" color={theme.mainWhite}>
              {memberDetailed.emergencyInformation}
            </HUText>
          ) : (
            <HUText fontStyle="TS" color={theme.mainWhite}>
              {word("box.member.table.profile.emergencyContactNotProvided")}
            </HUText>
          )}
        </div>
      </div>

      {memberDetailed.publicProfile && <PublicProfileDetails publicProfile={memberDetailed.publicProfile} />}
    </div>
  )
}
